import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from './PriceFilterBar.module.css';

export interface PriceRange {
    min: number;
    max: number;
}

export const PRICE_STEP = 1000;

interface PriceFilterBarProps {
    /** Cheapest/most expensive player in the pool, already snapped to PRICE_STEP. */
    bounds: PriceRange;
    value: PriceRange;
    onChange: (range: PriceRange) => void;
    /** Most a player may cost while the rest of the squad is still affordable. Null when the squad is full. */
    affordableCeiling: number | null;
    /** Empty slots left, for the budget hint. */
    slotsLeft: number;
    shown: number;
    total: number;
}

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', minimumFractionDigits: 0 }).format(amount);

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Editable price field. Keeps its own draft while the user types so a half-typed
 * number never clamps itself out from under the caret; commits on blur/Enter.
 */
const PriceField: React.FC<{
    value: number;
    ariaLabel: string;
    onCommit: (value: number) => void;
}> = ({ value, ariaLabel, onCommit }) => {
    const [draft, setDraft] = useState<string>(String(value));

    useEffect(() => setDraft(String(value)), [value]);

    const commit = () => {
        const parsed = parseInt(draft.replace(/\D/g, ''), 10);
        if (isNaN(parsed)) {
            setDraft(String(value));
            return;
        }
        onCommit(parsed);
    };

    return (
        <div className={styles.field}>
            <input
                className={styles.input}
                type="number"
                inputMode="numeric"
                aria-label={ariaLabel}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
            />
            <span className={styles.suffix}>kr.</span>
        </div>
    );
};

const PriceFilterBar: React.FC<PriceFilterBarProps> = ({
    bounds, value, onChange, affordableCeiling, slotsLeft, shown, total,
}) => {
    const { t } = useTranslation('pages');

    const span = Math.max(1, bounds.max - bounds.min);
    const pct = (v: number) => ((clamp(v, bounds.min, bounds.max) - bounds.min) / span) * 100;

    const isFiltered = value.min > bounds.min || value.max < bounds.max;
    const budgetActive = affordableCeiling !== null
        && value.min === bounds.min
        && value.max === clamp(affordableCeiling, bounds.min, bounds.max);

    const setMin = (v: number) => onChange({ min: clamp(v, bounds.min, value.max), max: value.max });
    const setMax = (v: number) => onChange({ min: value.min, max: clamp(v, value.min, bounds.max) });

    const applyBudget = () => {
        if (affordableCeiling === null) return;
        onChange({ min: bounds.min, max: clamp(affordableCeiling, bounds.min, bounds.max) });
    };

    return (
        <div className={styles.bar}>
            <span className={styles.label}>{t('fantasy.priceFilter.label')}</span>

            <div className={styles.fields}>
                <PriceField value={value.min} ariaLabel={t('fantasy.priceFilter.minLabel')} onCommit={setMin} />
                <span className={styles.fieldDash}>–</span>
                <PriceField value={value.max} ariaLabel={t('fantasy.priceFilter.maxLabel')} onCommit={setMax} />
            </div>

            <div className={styles.sliderWrap}>
                <div className={styles.track} />
                <div className={styles.fill} style={{ left: `${pct(value.min)}%`, right: `${100 - pct(value.max)}%` }} />
                <input
                    type="range"
                    className={styles.range}
                    aria-label={t('fantasy.priceFilter.minLabel')}
                    min={bounds.min}
                    max={bounds.max}
                    step={PRICE_STEP}
                    value={value.min}
                    onChange={(e) => setMin(Number(e.target.value))}
                    /* Lift the low handle above the high one once they meet at the top,
                       otherwise it gets buried and can never be dragged back down. */
                    style={{ zIndex: value.min >= bounds.max - PRICE_STEP ? 4 : 2 }}
                />
                <input
                    type="range"
                    className={styles.range}
                    aria-label={t('fantasy.priceFilter.maxLabel')}
                    min={bounds.min}
                    max={bounds.max}
                    step={PRICE_STEP}
                    value={value.max}
                    onChange={(e) => setMax(Number(e.target.value))}
                    style={{ zIndex: 3 }}
                />
            </div>

            <div className={styles.chips}>
                {affordableCeiling !== null && (
                    <button
                        type="button"
                        className={`${styles.chip} ${budgetActive ? styles.chipActive : ''}`}
                        onClick={applyBudget}
                        title={t('fantasy.priceFilter.budgetHint', { count: Math.max(0, slotsLeft - 1) })}
                    >
                        {t('fantasy.priceFilter.budget')} · ≤ {formatCurrency(Math.max(0, affordableCeiling))}
                    </button>
                )}
                {isFiltered && (
                    <button
                        type="button"
                        className={`${styles.chip} ${styles.chipGhost}`}
                        onClick={() => onChange({ ...bounds })}
                    >
                        {t('fantasy.priceFilter.reset')}
                    </button>
                )}
            </div>

            <span className={`${styles.count} ${isFiltered ? styles.countActive : ''}`}>
                {t('fantasy.priceFilter.showing', { shown, total })}
            </span>
        </div>
    );
};

export default PriceFilterBar;
